import { useTranslation } from 'react-i18next';
import { SPECIAL_AGENT_IDS, useAgentVisibility } from '@/entities/agent';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { getCurrentOrganizationId } from '@/entities/user';
import { useContentStudioAllowed } from '@/shared/config/useContentStudioAllowed';
import { StaggerItem } from '@/shared/ui/animations';
import { FeaturedCard } from '../FeaturedCard';
import { FEATURED_CARDS, OPPORTUNITIES_ROOM_FEATURED_CARD } from '../../model';

// Each card is its own step in the page cascade, entering alongside the catalog
// grid (index 3). The row gets no StaggerItem of its own: a rising wrapper would
// compound each card's translate.
const CARDS_STAGGER_START = 3;

interface FeaturedCardsRowProps {
  /**
   * Gives each card a call to action, shown in place of the Featured badge on
   * hover. What it says follows the card: an agent card opens a chat, the
   * rest open a surface.
   */
  showCta?: boolean;
}

export const FeaturedCardsRow = ({ showCta = false }: FeaturedCardsRowProps = {}) => {
  const { t } = useTranslation('tools');
  const workspaceId = useCurrentWorkspaceId();
  const organizationId = getCurrentOrganizationId();
  const { isAllowed: isContentStudioAllowed, isLoading: isContentStudioLoading } =
    useContentStudioAllowed();

  const { isAgentEnabled } = useAgentVisibility(
    [SPECIAL_AGENT_IDS.DEEP_RESEARCH_OPPORTUNITY_IDENTIFIER],
    {
      workspaceId,
      organizationId,
    },
  );
  const isOpportunitiesRoomAllowed = isAgentEnabled(
    SPECIAL_AGENT_IDS.DEEP_RESEARCH_OPPORTUNITY_IDENTIFIER,
  );

  // Show the Room card only where the tool is allowed, otherwise drop the slot.
  const opportunitiesCards = isOpportunitiesRoomAllowed ? [OPPORTUNITIES_ROOM_FEATURED_CARD] : [];

  const visibleCards = [
    ...opportunitiesCards,
    ...FEATURED_CARDS.filter((card) => {
      if (card.showOnHub === false) return false;
      if (card.requiresFeatureFlag === 'content_studio_mvp') {
        return !isContentStudioLoading && isContentStudioAllowed;
      }
      return true;
    }),
  ];

  // No element at all, so the page's flex gap does not leave an empty row.
  if (visibleCards.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-grid w-full">
      {visibleCards.map((card, index) => (
        <StaggerItem key={card.id} index={CARDS_STAGGER_START + index}>
          <FeaturedCard
            id={card.id}
            title={card.title}
            description={card.description}
            poster={card.poster}
            video={card.video}
            category={card.category}
            badgeIcon={card.badgeIcon}
            linkTo={card.linkTo}
            ctaLabel={
              showCta
                ? card.kind === 'agent'
                  ? t('toolsHub.toolCatalog.startChat')
                  : t('toolsHub.toolCatalog.openApp')
                : undefined
            }
            index={index}
          />
        </StaggerItem>
      ))}
    </div>
  );
};
